import type { PointerEvent, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Portal } from 'react-portal';
import { Icon } from '@anyroad/icon';
import { colors, mobile, overlays } from '@anyroad/theme';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const zoomIn = keyframes`
  from {
    opacity: 0;
    transform: scale3d(0.7, 0.7, 0.7);
  }
  to {
    opacity: 1;
  }
`;

const zoomOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
    transform: scale3d(0.7, 0.7, 0.7);
  }
`;

type StyledProps = {
  animationName: string;
  animationSpeed: string;
  padding?: string;
  width?: string;
};

const Background = styled.div<StyledProps>`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  display: grid;
  align-items: center;
  overflow-y: auto;
  overflow-x: hidden;

  background: ${overlays.primary};

  z-index: 1000000;

  animation-name: ${(props) => props.animationName};
  animation-duration: ${(props) => props.animationSpeed};
  animation-fill-mode: both;

  @media (min-width: ${mobile.phoneWidth}px) {
    overflow-y: auto;
  }
`;

const ModalMain = styled.div<StyledProps>`
  box-sizing: border-box;
  background: white;
  position: relative;

  animation-name: ${(props) => props.animationName};
  animation-duration: ${(props) => props.animationSpeed};
  animation-fill-mode: both;

  /* Needs to be 100vh rather than 100% for iOS */
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  padding: ${(props) => (props.padding ? props.padding : '20px')};
  margin: auto;

  @media (min-width: ${mobile.phoneWidth}px) {
    min-height: initial;
    width: ${(props) => props.width ?? '700px'};
    max-width: 90vw;
    padding: ${(props) => (props.padding ? props.padding : '40px 50px 50px')};
    margin: 50px auto;
  }
`;

type CloseProps = {
  isMobileCloseButtonHidden?: boolean;
};

export const CloseButton = styled.div<CloseProps>`
  /* Temporary hack to use this close button with the old modal until we replace that modal */
  display: ${(props) => (props.isMobileCloseButtonHidden ? 'none' : 'flex')};
  align-items: center;
  justify-content: center;
  border: 1px solid white;
  border-radius: 50%;
  height: 56px;
  width: 56px;

  cursor: pointer;
  z-index: 1000;

  position: fixed;
  top: 0;
  right: 0;
  background: white;

  svg {
    transform: scale(1.333);
    fill: #999;
  }

  @media (min-width: ${mobile.phoneWidth}px) {
    display: flex;
    position: absolute;
    height: 32px;
    width: 32px;
    top: -16px;
    right: -16px;
    background: ${colors.background.dark};
    svg {
      transform: scale(1.2);
      fill: white;
    }
  }
`;

export type ModalProps = {
  children: ReactNode;
  className: string;
  closeSpeed: number;
  isBackgroundClickable: boolean;
  isCloseButtonHidden: boolean;
  isClosing: boolean;
  onClose(): void;
  openSpeed: number;
  padding?: string;
  width?: string;
};

const Modal = (props: ModalProps) => {
  const {
    children,
    className,
    onClose,
    isCloseButtonHidden,
    isClosing,
    openSpeed,
    closeSpeed,
    isBackgroundClickable,
    padding,
    width,
  } = props;

  const bgRef = useRef<HTMLDivElement>(null);
  const allowClose = useRef(false);

  const setAllowClose = (bool: boolean) => {
    allowClose.current = bool;
  };

  useEffect(() => {
    window.document.body.style.overflowY = 'hidden';
    return () => {
      window.document.body.style.overflowY = 'initial';
    };
  }, []);

  const animationSpeed = isClosing ? `${closeSpeed}ms` : `${openSpeed}ms`;
  const isCloseButtonVisible = !isCloseButtonHidden;

  return (
    <Portal>
      <Background
        animationName={isClosing ? fadeOut : fadeIn}
        animationSpeed={animationSpeed}
        onPointerDown={(e: PointerEvent) => {
          if (!isBackgroundClickable) return;

          // Handles clicking on vertical scroll bar:
          if (e.clientX >= (e.target as HTMLDivElement).clientWidth) {
            setAllowClose(false);
          } else {
            setAllowClose(true);
          }
        }}
        onPointerUp={(e: PointerEvent) => {
          if (!isBackgroundClickable) return;

          if (
            bgRef.current === e.target &&
            allowClose.current &&
            e.clientX < (e.target as HTMLDivElement).clientWidth
          ) {
            onClose();
          }
        }}
        ref={bgRef}
      >
        <ModalMain
          animationName={isClosing ? zoomOut : zoomIn}
          animationSpeed={animationSpeed}
          className={className}
          data-testid="modal-main-overlay"
          onPointerDown={(e) => {
            e.stopPropagation();
            setAllowClose(false);
          }}
          padding={padding}
          width={width}
        >
          <div data-testid="modal-content-card">
            {isCloseButtonVisible ? (
              <div data-testid="cancel-message-btn">
                <CloseButton
                  data-testid="modal-main-close-button"
                  onClick={onClose}
                >
                  <Icon name="Close" />
                </CloseButton>
              </div>
            ) : null}
            {children}
          </div>
        </ModalMain>
      </Background>
    </Portal>
  );
};

export type ReactModalProps = {
  className?: string;
  closeSpeed?: number;
  isBackgroundClickable?: boolean;
  isCloseButtonHidden?: boolean;
  isOpen?: boolean;
  openSpeed?: number;
  padding?: string;
  width?: string;
};

export type ModalWrapperProps = ReactModalProps & {
  isOpen: boolean;
  onClose(): void;
};

/* eslint-disable-next-line func-style */
export function BaseModal<CustomProps>(
  ModalContent: (props: any) => JSX.Element,
  defaultProps: ReactModalProps = {}
): (props: CustomProps & ModalWrapperProps) => JSX.Element | null {
  const defaults = {
    className: '',
    isOpen: false,
    isCloseButtonHidden: false,
    openSpeed: 260,
    closeSpeed: 200,
    isBackgroundClickable: true,
    ...defaultProps,
  };

  const ModalWrapper = (props: CustomProps & ModalWrapperProps) => {
    const {
      className = defaults.className,
      isOpen = defaults.isOpen,
      onClose,
      isCloseButtonHidden = defaults.isCloseButtonHidden,
      openSpeed = defaults.openSpeed,
      closeSpeed = defaults.closeSpeed,
      isBackgroundClickable = defaults.isBackgroundClickable,
      padding = defaults.padding,
      width = defaults.width,
      ...otherProps
    } = props;

    const [isClosing, setIsClosing] = useState(false);
    const [isModalOpen, setModalOpen] = useState(isOpen);

    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleClose = useCallback(() => {
      setIsClosing(true);
      timeoutRef.current = setTimeout(() => {
        setIsClosing(false);
        setModalOpen(false);
      }, closeSpeed);
    }, [closeSpeed]);

    useEffect(
      () => () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      },
      []
    );

    useEffect(() => {
      if (isOpen) {
        // Cancel any pending close timer so a stale setModalOpen(false) from
        // an in-flight handleClose() can't flip a freshly-opened modal closed.
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        setIsClosing(false);
        setModalOpen(true);
      } else {
        handleClose();
      }
    }, [isOpen, handleClose]);

    return isModalOpen ? (
      <Modal
        className={className}
        closeSpeed={closeSpeed}
        isBackgroundClickable={isBackgroundClickable}
        isCloseButtonHidden={isCloseButtonHidden}
        isClosing={isClosing}
        onClose={onClose}
        openSpeed={openSpeed}
        padding={padding}
        width={width}
      >
        <ModalContent onClose={onClose} {...otherProps} />
      </Modal>
    ) : null;
  };

  return ModalWrapper;
}
